import {captureException} from "@sentry/react";
import {useEffect, useReducer, useState} from "react";
import useNotification from "v4/hooks/useNotification";
import fetchReducer from "v4/reducers/fetchReducer";
import {useTranslation} from "react-i18next";
import {useAuthContext} from "v4/contexts/AuthContext";
import {OUTDATED_FRONT_TYPE, outdatedFrontState} from "v4/contexts/AppVersionContext";

const errorMessage = (status, method, message = '') => {
    const messages = {
        POST: 'fetch_post_error',
        GET: 'fetch_get_error',
        403: 'rights_access_deny',
        404: 'error_custom_action_not_found',
        500: 'server_error',
    }

    return messages[status] ?? messages[method] ?? message ?? 'fetch_error';
};

const defaultConfig = {
    method: 'GET',
    headers: {
        Accept: 'application/ld+json',
        'Content-Type': 'application/json'
    },
    body: null,
};

/**
 * @typedef {Object} FetchState
 * @property {Response} rawResponse
 * @property {boolean} isLoading
 * @property {boolean} isFinished
 * @property {boolean} isError
 * @property {Object} data
 * @property {Object} errorData
 */

/**
 * @typedef {Object} FetchFunctionProp
 * @property {string} url
 * @property {{
 *     [method]: string,
 *     [headers]: Object,
 *     [body]: FormData|string,
 *     [signal]: AbortSignal,
 *     [hasToPatchSymfony]: boolean,
 *     }} [config]
 */

/**
 * @param {("json"|"blob")} type
 * @param {{
 *     ignoreErrorMessages: boolean,
 * }} [hookConfig]
 * @returns {[FetchState, function(FetchFunctionProp)]} [state, fetch]
 */
export default function useFetch(type = 'json', hookConfig = {}) {
    const {logOut} = useAuthContext();

    if (!['json', 'blob'].includes(type)) {
        const err = new Error('Type must be json or blob');
        captureException(err);
        throw err;
    }

    const {addErrorNotification} = useNotification();
    const {t} = useTranslation();

    const notifyError = (message) => {
        if (hookConfig.ignoreErrorMessages || outdatedFrontState.isOutdated) return;

        addErrorNotification(message);
    };

    const [fetchInfos, setFetchInfos] = useState({
        url: null,
        config: {}
    });

    const [state, dispatch] = useReducer(fetchReducer, {
        data: null,
        rawResponse: null,
        isLoading: false,
        isError: false,
        isFinished: false,
        token: null,
        errorData: null,
    });

    const abortController = new AbortController();

    const polFetch = async ({url, config = {}}) => {
        const {hasToPatchSymfony, ...restConfig} = config;
        const finalConfig = {
            ...JSON.parse(JSON.stringify(defaultConfig)), ...restConfig,
            signal: abortController.signal
        };

        // Si les données sont envoyées via FormData, on retire le Content-Type pour
        // laisser le navigateur gérer le multipart/form-data
        if (finalConfig.body instanceof FormData) {
            const {'Content-Type': _, ...headers} = finalConfig.headers;
            finalConfig.headers = headers;
        }

        if (hasToPatchSymfony && finalConfig.method === 'PUT') {
            finalConfig.method = 'POST';

            if (finalConfig.body instanceof FormData) {
                finalConfig.body.append('_method', 'PUT');
            }

            // If body is a string, it's a JSON string
            if (typeof finalConfig.body === 'string') {
                finalConfig.body = JSON.stringify({...JSON.parse(finalConfig.body), _method: 'PUT'});
            }
        }

        dispatch({type: 'FETCH_INIT'});

        try {
            const rawResponse = await fetch(url, finalConfig);

            if (rawResponse.status >= 200 && rawResponse.status < 300) {
                if (finalConfig.method === 'DELETE' || rawResponse.status === 204) return dispatch({
                    type: "FETCH_SUCCESS",
                    payload: {rawResponse}
                });


                const data = await rawResponse[type]();

                // If type is blob, we create a File object with it
                if (type === 'blob') {
                    const disposition = rawResponse.headers.get('Content-Disposition');
                    const filenameMatch = disposition && disposition.match(/filename="?([^"]+)"?/);
                    const filename = (filenameMatch && filenameMatch[1]) || 'file_download';
                    const blobType = rawResponse.headers.get('Content-Type') ?? 'application/octet-stream';

                    return dispatch({
                        type: "FETCH_SUCCESS",
                        payload: {data: new File([data], filename, {type: blobType}), rawResponse}
                    });
                }

                return dispatch({type: "FETCH_SUCCESS", payload: {data, rawResponse}});
            }

            if (rawResponse.status >= 400 && rawResponse.status < 600) {
                let json = null;

                try {
                    json = await rawResponse.json();
                } catch (e) {
                    // ignore parsing error, json remains null
                }

                if (rawResponse.status === 410 && json?.type === OUTDATED_FRONT_TYPE) {
                    return dispatch({type: "FETCH_FAILURE", payload: {data: json, rawResponse}});
                }

                if ('errors' in (json ?? {})) {
                    const defaultErrorMessages = json?.errors['_default']?.messages ?? [];

                    defaultErrorMessages.forEach((message) => {
                        notifyError(t(errorMessage(null, null, message)));
                    });
                } else if ('details' in (json ?? {})) {
                    notifyError(json?.details);
                } else if (rawResponse.status !== 401 && json?.status !== 401) {
                    notifyError(t(errorMessage(rawResponse.status, finalConfig.method, json?.message)));
                } else if (json?.status === 401) {
                    logOut();
                    addErrorNotification(t('expired_token'));
                    return;
                }

                return dispatch({type: "FETCH_FAILURE", payload: {data: json, rawResponse}});
            }
        } catch (e) {
            if (e.name === 'AbortError') return;

            if (e.name === 'TokenExpired') {
                logOut();
                addErrorNotification(t('expired_token'));
                return;
            }

            notifyError(t(errorMessage(null, finalConfig.method)));
            return dispatch({type: "FETCH_FAILURE"});
        }
    }

    useEffect(() => {
        if (fetchInfos.url) {
            polFetch(fetchInfos);
        }

        return () => abortController.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchInfos]);

    return [state, setFetchInfos];
}
